import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";
import { adminAnalyticsService } from "../../../../services/adminAnalyticsService";
import SummaryCards from "../SummaryCards";
import {
  formatDateTime,
  getFieldValue,
  getSummaryEntries,
} from "../analyticsUtils";
import { SectionError, SectionLoading } from "../SectionState";

const USER_COLUMNS = [
  {
    key: "email",
    label: "Email",
    render: (_, row) => getFieldValue(row, ["email"]),
  },
  {
    key: "first_name",
    label: "First Name",
    render: (_, row) => getFieldValue(row, ["first_name", "firstName"]),
  },
  {
    key: "last_name",
    label: "Last Name",
    render: (_, row) => getFieldValue(row, ["last_name", "lastName"]),
  },
  {
    key: "created_at",
    label: "Created At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["created_at", "createdAt"])),
  },
  {
    key: "last_login_at",
    label: "Last Login At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["last_login_at", "lastLoginAt"])),
  },
  {
    key: "last_activity_at",
    label: "Last Activity At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["last_activity_at", "lastActivityAt"])),
  },
];

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}%`;
};

const clampPercent = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
};

const getStageKey = (stage, index) =>
  stage?.stage || stage?.module_id || `${stage?.label || "stage"}-${index}`;

const SHOW_COURSE_FILTER = false;

const StageUsersTable = ({ users = [] }) => {
  if (!users.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm italic text-slate-500">
        No users returned for this stage.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {USER_COLUMNS.map((column) => (
              <th
                key={column.key}
                className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <tr key={user.user_id || user.id || index} className="hover:bg-slate-50/70">
              {USER_COLUMNS.map((column) => (
                <td
                  key={column.key}
                  className="border-b border-slate-100 px-4 py-3 align-top text-slate-700"
                >
                  {column.render
                    ? column.render(user[column.key], user)
                    : user[column.key] ?? "--"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProgressionFunnelPanel = ({ paidOnly = false }) => {
  const [query, setQuery] = useState({
    course_filter_type: "course_slug",
    course_filter_value: "",
  });
  const [draft, setDraft] = useState(query);
  const [data, setData] = useState(null);
  const [expandedStageKey, setExpandedStageKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestParams = useMemo(() => {
    const params = { paid_only: paidOnly };
    const courseValue = String(query.course_filter_value || "").trim();

    if (courseValue) {
      params[query.course_filter_type] = courseValue;
    }

    return params;
  }, [paidOnly, query]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await adminAnalyticsService.getProgressionFunnelReport(
        requestParams
      );
      setData(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load progression funnel.");
    } finally {
      setLoading(false);
    }
  }, [requestParams]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const stages = useMemo(
    () => (Array.isArray(data?.stages) ? data.stages : []),
    [data]
  );

  useEffect(() => {
    if (!stages.length) {
      setExpandedStageKey("");
      return;
    }

    const hasExpandedStage = stages.some(
      (stage, index) => getStageKey(stage, index) === expandedStageKey
    );

    if (!hasExpandedStage) {
      setExpandedStageKey("");
    }
  }, [expandedStageKey, stages]);

  const summaryEntries = useMemo(() => getSummaryEntries(data), [data]);

  const onApply = (event) => {
    event.preventDefault();
    setQuery(draft);
  };

  return (
    <section className="space-y-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-2xl font-semibold text-slate-900">
            Progression Funnel
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Review learner movement from signup and login through module completion.
          </p>
        </div>
        {data?.course && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <div className="font-semibold text-slate-900">
              {data.course.title || "Selected course"}
            </div>
            <div className="mt-1 font-mono text-xs">
              {data.course.slug || data.course.id}
            </div>
          </div>
        )}
      </div>

      {SHOW_COURSE_FILTER && (
        <form
          onSubmit={onApply}
          className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4"
        >
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,180px)_1fr_auto] xl:items-end">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Course Filter
              </span>
              <select
                value={draft.course_filter_type}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    course_filter_type: e.target.value,
                  }))
                }
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
              >
                <option value="course_slug">Course Slug</option>
                <option value="course_id">Course ID</option>
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Course Value
              </span>
              <input
                type="text"
                value={draft.course_filter_value}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    course_filter_value: e.target.value,
                  }))
                }
                placeholder="Leave blank for all users"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
              />
            </label>

            <button
              type="submit"
              className="min-w-[160px] rounded-xl bg-[#54B0AF] px-5 py-2.5 text-sm font-medium text-white shadow hover:opacity-90"
              disabled={loading}
            >
              Apply
            </button>
          </div>
        </form>
      )}

      {loading && <SectionLoading text="Loading progression funnel..." />}
      {error && <SectionError message={error} />}

      {!loading && !error && (
        <div className="space-y-4" data-testid="progression-funnel-content">
          <SummaryCards entries={summaryEntries} />

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_10px_40px_rgba(15,23,42,0.06)]">
            <div className="mb-5">
              <h4 className="text-lg font-semibold text-slate-900">Stages</h4>
              <p className="mt-1 text-sm text-slate-500">
                Select a stage to inspect the users returned by the backend.
              </p>
            </div>

            {!stages.length ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm italic text-slate-500">
                No funnel stages found.
              </div>
            ) : (
              <div className="space-y-3">
                {stages.map((stage, index) => {
                  const stageKey = getStageKey(stage, index);
                  const isExpanded = stageKey === expandedStageKey;
                  const pctOfUsers = clampPercent(stage.pct_of_users);
                  const stageUsers = Array.isArray(stage.users) ? stage.users : [];

                  return (
                    <div
                      key={stageKey}
                      className={`overflow-hidden rounded-2xl border transition ${
                        isExpanded
                          ? "border-[#54B0AF] bg-[#eefafa]"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedStageKey((current) =>
                            current === stageKey ? "" : stageKey
                          )
                        }
                        className="w-full p-4 text-left"
                        aria-expanded={isExpanded}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <ChevronRight
                              size={18}
                              className={`shrink-0 transition-transform ${
                                isExpanded ? "rotate-90 text-[#54B0AF]" : "text-slate-400"
                              }`}
                            />
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-900">
                                {stage.label || stage.stage || `Stage ${index + 1}`}
                              </div>
                              {(stage.module_title || stage.module_order) && (
                                <div className="mt-1 text-xs text-slate-500">
                                  {stage.module_order ? `Module ${stage.module_order}` : "Module"}
                                  {stage.module_title ? `: ${stage.module_title}` : ""}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4 text-right text-sm">
                            <div>
                              <div className="font-semibold text-slate-900">
                                {stage.count ?? "--"}
                              </div>
                              <div className="text-xs text-slate-500">Users</div>
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">
                                {formatPercent(stage.pct_of_users)}
                              </div>
                              <div className="text-xs text-slate-500">Of users</div>
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">
                                {formatPercent(stage.conversion_from_previous_pct)}
                              </div>
                              <div className="text-xs text-slate-500">Previous</div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-[#54B0AF]"
                            style={{ width: `${pctOfUsers}%` }}
                          />
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-slate-200 bg-white/80 p-4">
                          <StageUsersTable users={stageUsers} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProgressionFunnelPanel;
